import { useEffect, useState } from 'react'
import { Button, Card, Group, InputBase, Select, Stack, TextInput, Title } from '@mantine/core'
import { IMaskInput } from 'react-imask'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useMedplum } from '@medplum/react'
import type { Organization } from '@medplum/fhirtypes'
import { states } from '@/utils/states.ts'
import { logError } from '@/utils/errorUtils'
import { geocodeAddress } from './shared/geocodeAddress'
import { FacilityLogoField } from './shared/FacilityLogoField'

const LOGO_EXTENSION_URL = 'https://behuman.health/fhir/StructureDefinition/organization-logo'
const GEOLOCATION_EXTENSION_URL = 'http://hl7.org/fhir/StructureDefinition/geolocation'
const WEBSITE_EXTENSION_URL = 'https://behuman.health/fhir/StructureDefinition/organization-website'

interface CreateUpdateLabFacilityProps {
  organization?: Organization
  onSave?: (organization: Organization) => void
  onCancel?: () => void
}

interface LabFacilityFormData {
  locationName: string
  parentLabId: string
  parentLabName: string
  phone: string
  fax: string
  website: string
  address: {
    state: string
    zipCode: string
    city: string
    street: string
  }
}

function getExtensionValue(org: Organization | undefined, url: string): string {
  return org?.extension?.find((e) => e.url === url)?.valueString || ''
}

function getLogoReference(org: Organization | undefined): string {
  return org?.extension?.find((e) => e.url === LOGO_EXTENSION_URL)?.valueReference?.reference || ''
}

function extractLocationName(org: Organization | undefined): string {
  if (!org?.name) return ''
  const partOfDisplay = org.partOf?.display
  if (partOfDisplay && org.name.startsWith(`${partOfDisplay} - `)) {
    return org.name.slice(`${partOfDisplay} - `.length)
  }
  return org.name
}

export function CreateUpdateLabFacility({
  organization,
  onSave,
  onCancel,
}: CreateUpdateLabFacilityProps) {
  const medplum = useMedplum()
  const [isLoading, setIsLoading] = useState(false)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [parentLabs, setParentLabs] = useState<Organization[]>([])
  const [parentLabsLoading, setParentLabsLoading] = useState(true)
  const [parentLabsError, setParentLabsError] = useState(false)
  const isPending = organization && organization.active === false

  const initialParentLabId = organization?.partOf?.reference?.replace('Organization/', '') || ''
  const initialParentLabName = organization?.partOf?.display || ''

  const form = useForm<LabFacilityFormData>({
    initialValues: {
      locationName: extractLocationName(organization),
      parentLabId: initialParentLabId,
      parentLabName: initialParentLabName,
      phone: organization?.telecom?.find((t) => t.system === 'phone')?.value || '',
      fax: organization?.telecom?.find((t) => t.system === 'fax')?.value || '',
      website: getExtensionValue(organization, WEBSITE_EXTENSION_URL),
      address: {
        state: organization?.address?.[0]?.state || '',
        zipCode: organization?.address?.[0]?.postalCode || '',
        city: organization?.address?.[0]?.city || '',
        street: organization?.address?.[0]?.line?.[0] || '',
      },
    },
    validate: {
      locationName: (value) => (!value?.trim() ? 'Facility name is required' : null),
      parentLabId: (value) => (!value?.trim() ? 'Parent lab is required' : null),
      fax: (value) => {
        if (!value) return null
        const digits = value.replace(/\D/g, '')
        return digits.length === 10 ? null : 'Invalid fax number'
      },
      phone: (value) => {
        if (!value) return null
        const digits = value.replace(/\D/g, '')
        return digits.length === 10 ? null : 'Invalid phone number'
      },
      address: {
        state: (value) => (!value?.trim() ? 'State is required' : null),
        zipCode: (value) => {
          if (!value?.trim()) return 'ZIP code is required'
          const digits = value.replace(/\D/g, '')
          if (!(digits.length === 5 || digits.length === 9)) {
            return 'ZIP code must be 5 or 9 digits'
          }
          return null
        },
        city: (value) => (!value?.trim() ? 'City is required' : null),
        street: (value) => (!value?.trim() ? 'Street address is required' : null),
      },
    },
  })

  useEffect(() => {
    setParentLabsLoading(true)
    setParentLabsError(false)
    medplum
      .searchResources('Organization', 'type=laboratory&_count=1000')
      .then((orgs) => {
        setParentLabs(
          orgs.filter((o) => o.id && o.name) as (Organization & { id: string; name: string })[],
        )
      })
      .catch((err) => {
        logError('super', 'Failed to load parent labs', err)
        setParentLabsError(true)
      })
      .finally(() => setParentLabsLoading(false))
  }, [medplum])

  const handleSubmit = async (values: LabFacilityFormData, approve = false) => {
    let zip = values.address.zipCode || ''
    const zipDigits = zip.replace(/\D/g, '')
    if (zipDigits.length === 9) {
      zip = `${zipDigits.slice(0, 5)}-${zipDigits.slice(5)}`
    } else if (zipDigits.length === 5) {
      zip = zipDigits
    }

    const selectedParentLab = parentLabs.find((lab) => lab.id === values.parentLabId)
    const selectedParentLabName = selectedParentLab?.name || values.parentLabName
    const fullName = `${selectedParentLabName} - ${values.locationName.trim()}`

    setIsLoading(true)
    try {
      // Geocode address
      let geoExtension: Organization['address'] extends (infer U)[]
        ? NonNullable<U>['extension']
        : never = undefined
      const coords = await geocodeAddress(medplum, {
        street: values.address.street,
        city: values.address.city,
        state: values.address.state,
        zip,
      })
      if (coords) {
        geoExtension = [
          {
            url: GEOLOCATION_EXTENSION_URL,
            extension: [
              { url: 'latitude', valueDecimal: coords.lat },
              { url: 'longitude', valueDecimal: coords.lon },
            ],
          },
        ]
      }

      // Upload logo if provided
      let logoReference = getLogoReference(organization)
      if (logoFile) {
        const arrayBuffer = await logoFile.arrayBuffer()
        const base64 = btoa(
          new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), ''),
        )
        const binary = await medplum.createResource({
          resourceType: 'Binary',
          contentType: logoFile.type,
          data: base64,
        })
        if (binary.id) {
          logoReference = `Binary/${binary.id}`
        }
      }

      const faxDigits = values.fax.replace(/\D/g, '')

      // Preserve existing geolocation if geocoding didn't run
      if (!geoExtension && organization?.address?.[0]?.extension) {
        geoExtension = organization.address[0].extension
      }

      const extensions: Organization['extension'] = []

      if (values.website.trim()) {
        extensions.push({
          url: WEBSITE_EXTENSION_URL,
          valueString: values.website.trim(),
        })
      }

      if (logoReference) {
        extensions.push({
          url: LOGO_EXTENSION_URL,
          valueReference: { reference: logoReference },
        })
      }

      // Preserve active status; set true if approving
      const activeStatus = approve ? true : (organization?.active ?? true)

      const organizationData: Organization = {
        resourceType: 'Organization',
        active: activeStatus,
        name: fullName,
        partOf: {
          reference: `Organization/${values.parentLabId}`,
          display: selectedParentLabName,
        },
        type: [
          {
            coding: [
              {
                system: 'http://terminology.hl7.org/CodeSystem/organization-type',
                code: 'prov',
                display: 'Healthcare Provider',
              },
            ],
          },
          {
            coding: [
              {
                system: 'https://behuman.health/organization-type',
                code: 'laboratory-location',
                display: 'Laboratory Location',
              },
            ],
          },
        ],
        telecom: [
          ...(faxDigits ? [{ system: 'fax' as const, value: values.fax }] : []),
          ...(values.phone.trim() ? [{ system: 'phone' as const, value: values.phone }] : []),
        ],
        address: [
          {
            line: [values.address.street],
            city: values.address.city,
            state: values.address.state,
            postalCode: zip,
            country: 'US',
            extension: geoExtension,
          },
        ],
        extension: extensions.length > 0 ? extensions : undefined,
      }

      if (!organization) {
        const behumanProject = await medplum.searchOne('Project', 'name:contains=behuman')
        if (!behumanProject) {
          throw new Error('Failed to get behuman project')
        }
        organizationData.meta = { project: behumanProject.id }
      } else if (organization.id) {
        organizationData.id = organization.id
        if (organization.meta?.project) {
          organizationData.meta = {
            ...organization.meta,
            project: organization.meta.project,
          }
        }
      }

      let result: Organization
      if (!organization) {
        result = await medplum.createResource(organizationData)
      } else {
        result = await medplum.updateResource(organizationData)
      }

      notifications.show({
        title: 'Success',
        message: `Lab facility ${!organization ? 'created' : 'updated'} successfully`,
        color: 'green',
      })

      onSave?.(result)
    } catch (error) {
      logError('super', `Error ${!organization ? 'creating' : 'updating'} lab facility`, error)
      notifications.show({
        title: 'Error',
        message: `Failed to ${!organization ? 'create' : 'update'} lab facility. Please try again.`,
        color: 'red',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const parentLabSelectData = parentLabs
    .filter((lab) => lab.id && lab.name)
    .map((lab) => ({ value: lab.id!, label: lab.name! }))

  return (
    <Card withBorder className="mx-auto max-w-2xl">
      <Title order={2} mb="md">
        {!organization ? 'Add Lab Facility' : 'Update Lab Facility'}
      </Title>

      <form onSubmit={form.onSubmit((v) => handleSubmit(v, false))}>
        <Stack gap="md">
          <Select
            label="Parent Lab"
            placeholder={
              parentLabsLoading
                ? 'Loading...'
                : parentLabsError
                  ? 'Failed to load labs'
                  : 'Select parent lab'
            }
            data={parentLabSelectData}
            value={form.values.parentLabId}
            onChange={(value) => {
              const lab = parentLabs.find((l) => l.id === value)
              form.setFieldValue('parentLabId', value ?? '')
              form.setFieldValue('parentLabName', lab?.name ?? '')
            }}
            error={form.errors.parentLabId}
            required
            searchable
            disabled={parentLabsLoading}
          />

          <TextInput
            label="Location Name"
            placeholder="e.g. Abbeville (Ozark Rd)"
            {...form.getInputProps('locationName')}
            required
          />

          <Group grow>
            <TextInput
              label="Street Address"
              placeholder="Street address"
              {...form.getInputProps('address.street')}
              required
            />
            <TextInput
              label="City"
              placeholder="City"
              {...form.getInputProps('address.city')}
              required
            />
          </Group>
          <Group grow>
            <Select
              label="State"
              placeholder="Select state"
              data={states}
              {...form.getInputProps('address.state')}
              required
              searchable
            />
            <InputBase
              label="ZIP Code"
              placeholder="00000 or 12345-1234"
              component={IMaskInput}
              mask={[{ mask: '00000' }, { mask: '00000-0000' }]}
              {...form.getInputProps('address.zipCode')}
              required
            />
          </Group>

          <Group grow>
            <InputBase
              label="Phone"
              placeholder="(000) 000-0000"
              component={IMaskInput}
              mask="(000) 000-0000"
              value={form.values.phone}
              onAccept={(value) => form.setFieldValue('phone', value)}
              error={form.errors.phone}
            />
            <InputBase
              label="Fax"
              placeholder="(000) 000-0000"
              component={IMaskInput}
              mask="(000) 000-0000"
              value={form.values.fax}
              onAccept={(value) => form.setFieldValue('fax', value)}
              error={form.errors.fax}
            />
          </Group>

          <TextInput
            label="Website"
            placeholder="https://example.com"
            {...form.getInputProps('website')}
          />

          <FacilityLogoField
            value={logoFile}
            onChange={setLogoFile}
            existingLogoRef={getLogoReference(organization)}
          />

          <Group justify="flex-end" mt="xl">
            {onCancel && (
              <Button variant="subtle" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button type="submit" loading={isLoading} disabled={parentLabsLoading}>
              {!organization ? 'Add Facility' : 'Update Facility'}
            </Button>
            {isPending && (
              <Button
                type="button"
                color="green"
                loading={isLoading}
                disabled={parentLabsLoading}
                onClick={() => form.onSubmit((v) => handleSubmit(v, true))()}
              >
                Approve & Save
              </Button>
            )}
          </Group>
        </Stack>
      </form>
    </Card>
  )
}
